function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}
class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      event.preventDefault();
      this.closest("cart-items").updateQuantity(this.dataset.index, 0);
    });
  }
}

if (!customElements.get("cart-remove-button")) {
  customElements.define("cart-remove-button", CartRemoveButton);
}

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById(
      "shopping-cart-line-item-status"
    );
    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce(
      (total, quantityInput) => total + parseInt(quantityInput.value),
      0
    );

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    // Update visibility free gift progressbar
    this.updateProgressBarVisibility();

    this.addEventListener("change", this.debouncedOnChange.bind(this));
  }

  updateProgressBarVisibility() {
    // Update visibility free gift progress bar
    let giftCards = document.querySelectorAll('[data-item-type="giftcard"]');
    const progressBar = document.querySelector("progress-bar");
    const freeGift = document.querySelector("free-gift");

    // if there are no gift cards in the cart
    if (giftCards.length === 0) {
      if (progressBar && freeGift) {
        progressBar.classList.remove("hidden");
        freeGift.classList.remove("hidden");
      }
    } else {
      if (progressBar && freeGift) {
        progressBar.classList.add("hidden");
        freeGift.classList.add("hidden");
      }
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target,
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute("name")
    );
  }

  getSectionsToRender() {
    // if on mobile
    if (window.screen.width < 768) {
      return [
        {
          id: "main-cart-mobile-buttons",
          section: document.getElementById("main-cart-mobile-buttons").dataset.id,
          selector: ".js-cart-mobile-buttons",
        },
        {
          id: "main-cart-item-count",
          section: document.getElementById("main-cart-item-count").dataset.id,
          selector: ".js-cart-item-count",
        },
        {
          id: "main-cart-items",
          section: document.getElementById("main-cart-items").dataset.id,
          selector: ".js-cart-item-contents",
        },
        {
          id: "main-cart-footer",
          section: document.getElementById("main-cart-footer").dataset.id,
          selector: ".js-cart-footer-contents",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: "[data-cart-icon-bubble]",
        },
      ];
    } else {
      return [
        {
          id: "main-cart-item-count",
          section: document.getElementById("main-cart-item-count").dataset.id,
          selector: ".js-cart-item-count",
        },
        {
          id: "main-cart-items",
          section: document.getElementById("main-cart-items").dataset.id,
          selector: ".js-cart-item-contents",
        },
        {
          id: "main-cart-footer",
          section: document.getElementById("main-cart-footer").dataset.id,
          selector: ".js-cart-footer-contents",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: "[data-cart-icon-bubble]",
        },
      ];
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) ||
        document.getElementById(section.id);
      if (elementToReplace) {
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      }
    });
  }

  updateContents(parsedState) {
    // Safely handle cases where items might be undefined
    const items = parsedState?.items || [];
    // Count only non-free gift items to determine empty state
    const nonFreeGiftItems = items.filter(
      (item) => !item.properties?._free_gift
    );
    const isEmpty = nonFreeGiftItems.length === 0;

    this.classList.toggle(
      parsedState.sections["cart-drawer"] ? "cart-drawer-is-empty" : "is-empty",
      isEmpty
    );
    document
      .getElementById("main-cart-footer")
      ?.classList.toggle("is-empty", isEmpty);
  }

  updateQuantity(input, line, quantity, name) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (!parsedState.sections) {
          return Promise.reject(parsedState.errors);
        }

        // Get the line as integer
        const lineNumber = parseInt(line);

        // Find the main product line by comparing index (position-1)
        // Shopify uses 1-based indexing for the line parameter, but 0-based for array indices
        const mainProductLine = parsedState.items[lineNumber - 1];

        // For removal (quantity = 0), we don't need the mainProductLine to exist
        if (quantity == 0) {
          // IMPORTANT: We need to capture the variant ID BEFORE the fetch request
          // because the item will be gone from the response after removal
          const itemElement = document.querySelector(
            `[data-line-index="${lineNumber}"]`
          );
          const mainProductVariantId = itemElement?.dataset.variantId;

          // After removing a product, we need to recalculate free product quantities
          // Get fresh cart data to see what's left
          return fetch(`${routes.cart_url}.js`)
            .then((response) => response.json())
            .then((freshCart) => {
              // Find all free products in the cart
              const cartFreeProducts = freshCart.items.filter(
                (item) => item.properties?._free_product === "true"
              );

              if (cartFreeProducts.length > 0) {
                // Count qualifying products that remain
                const qualifyingProducts = freshCart.items.filter(
                  (item) =>
                    !item.properties?._free_product &&
                    !item.properties?._free_gift &&
                    !item.gift_card
                );

                // Calculate total needed
                const totalNeeded = qualifyingProducts.reduce(
                  (sum, item) => sum + item.quantity,
                  0
                );

                let updatePromise = Promise.resolve(parsedState);

                // Update each free product to match the total quantity needed
                for (const freeProduct of cartFreeProducts) {
                  const freeProductIndex =
                    freshCart.items.indexOf(freeProduct) + 1;

                  if (totalNeeded > 0) {
                    if (freeProduct.quantity !== totalNeeded) {
                      updatePromise = updatePromise.then((state) => {
                        return this.updateQuantityOnly(
                          freeProductIndex,
                          totalNeeded,
                          state
                        );
                      });
                    }
                  } else {
                    // Remove free product if no qualifying products remain
                    updatePromise = updatePromise.then((state) => {
                      return this.updateQuantityOnly(
                        freeProductIndex,
                        0,
                        state
                      );
                    });
                  }
                }

                return updatePromise;
              }

              return parsedState;
            })
            .catch((error) => {
              console.error("Error refreshing cart after removal:", error);
              return parsedState;
            });
        }

        // For quantity > 0, we need the mainProductLine to exist
        if (!mainProductLine) {
          console.error("Main product not found");
          return Promise.reject("Main product not found");
        }

        const mainProductVariantId = mainProductLine.variant_id.toString();

        // For quantity updates other than removal, sync all free products
        if (quantity > 0) {
          // Return the current response, then chain a second request to update free products
          return Promise.resolve(parsedState).then((state) => {
            // Get latest cart state to avoid stale data
            return fetch(`${routes.cart_url}.js`)
              .then((response) => response.json())
              .then((freshCart) => {
                // Find all free products in the cart
                const cartFreeProducts = freshCart.items.filter(
                  (item) => item.properties?._free_product === "true"
                );

                if (cartFreeProducts.length === 0) return state;

                // Count all qualifying products
                const qualifyingProducts = freshCart.items.filter(
                  (item) =>
                    !item.properties?._free_product &&
                    !item.properties?._free_gift &&
                    !item.gift_card
                );

                // Calculate total needed across all qualifying products
                const totalNeeded = qualifyingProducts.reduce(
                  (sum, item) => sum + item.quantity,
                  0
                );

                // Update all free products to match the total
                let updatePromise = Promise.resolve(state);

                for (const freeProduct of cartFreeProducts) {
                  const freeProductIndex =
                    freshCart.items.indexOf(freeProduct) + 1;

                  if (freeProduct.quantity !== totalNeeded) {
                    updatePromise = updatePromise.then((state) =>
                      this.updateQuantityOnly(
                        freeProductIndex,
                        totalNeeded,
                        state
                      )
                    );
                  }
                }

                return updatePromise;
              })
              .catch((error) => {
                console.error("Error fetching fresh cart:", error);
                return state;
              });
          });
        }

        return parsedState;
      })
      .then((finalState) => {
        this.updateContents(finalState);
        this.renderContents(finalState);

        let giftCards = document.querySelectorAll(
          '[data-item-type="giftcard"]'
        );

        if (giftCards.length === 0) {
          // Update free gift
          const freeGift = document.querySelector("free-gift");
          freeGift?.classList.remove("hidden");
          freeGift?.update(finalState);

          // Update progress bar
          const progressBar = document.querySelector("progress-bar");
          progressBar?.classList.remove("hidden");
          progressBar?.update(finalState);
        }

        this.updateLiveRegions(line, finalState.item_count);
        document
          ?.getElementById(`CartItem-${line}`)
          ?.querySelector(`[name="${name}"]`)
          ?.focus();
      })
      .catch((error) => {
        this.querySelectorAll(".loading-overlay").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors = document.getElementById("cart-errors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading();
      });
  }

  // Helper method to update free products
  updateFreeProducts(freeProducts, state, quantity) {
    if (freeProducts.length === 0) {
      return state;
    }

    console.log(
      `Updating ${freeProducts.length} free product(s) to quantity ${quantity}`
    );

    // Create a chain of promises to update all associated free products
    let updatePromise = Promise.resolve(state);

    freeProducts.forEach((freeProduct) => {
      const freeProductIndex = state.items.indexOf(freeProduct) + 1;
      updatePromise = updatePromise.then((state) => {
        console.log(
          `Updating free product at index ${freeProductIndex} to quantity ${quantity}`
        );

        // Get the main product from the state or DOM
        let mainProductVariantId =
          freeProduct.properties?._main_product_variant ||
          freeProduct.properties?._parent_id;

        // Get DOM element for additional information if needed
        const freeProductElement = document.querySelector(
          `[data-free-product="true"][data-main-product-variant="${mainProductVariantId}"]`
        );
        const mainProductElement = document.querySelector(
          `[data-variant-id="${mainProductVariantId}"]`
        );

        console.log(
          "Updating free product with main product variant ID:",
          mainProductVariantId
        );
        console.log(
          "Free product element:",
          freeProductElement ? freeProductElement.outerHTML : "Not found"
        );
        console.log(
          "Main product element:",
          mainProductElement ? mainProductElement.outerHTML : "Not found"
        );

        // IMPORTANT: Only update the quantity, not the properties
        // This prevents UI issues with quantity selectors disappearing
        const freeProductBody = JSON.stringify({
          line: freeProductIndex.toString(),
          quantity: quantity,
          // Don't update properties to avoid UI rendering issues
          sections: this.getSectionsToRender().map(
            (section) => section.section
          ),
          sections_url: window.location.pathname,
        });

        return fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          ...{ body: freeProductBody },
        })
          .then((response) => response.text())
          .then((freeProductState) => {
            try {
              return JSON.parse(freeProductState);
            } catch (error) {
              console.error("Error parsing response:", error);
              return state;
            }
          })
          .catch((error) => {
            console.error("Error updating free product:", error);
            return state;
          });
      });
    });

    return updatePromise;
  }

  // Helper method to get variant ID from line number
  getVariantIdFromLine(lineNumber, parsedState) {
    // Check if we still have the variant in the cart state
    if (parsedState.items[lineNumber - 1]) {
      return parsedState.items[lineNumber - 1].variant_id.toString();
    }

    // If not (item was removed), we need to check DOM for the data
    const lineItem = document.querySelector(
      `[data-line-index="${lineNumber}"]`
    );
    if (lineItem) {
      return lineItem.dataset.mainProductVariant || null;
    }

    // If we can't find it in DOM either, check for item elements with free product data
    const itemWithFreeProduct = document.querySelector(
      '[data-has-free-product="true"]'
    );
    if (itemWithFreeProduct) {
      return itemWithFreeProduct.dataset.freeProductId || null;
    }

    return null;
  }

  // show error texts, should not be deleted.
  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      if (
        document
          ?.getElementById(`Line-item-error-${line}`)
          ?.querySelector(".cart-item__error-text") &&
        document?.getElementById(`Quantity-${line}`)?.value
      ) {
        document
          .getElementById(`Line-item-error-${line}`)
          .querySelector(".cart-item__error-text").innerHTML =
          window.cartStrings.quantityError.replace(
            "[quantity]",
            document.getElementById(`Quantity-${line}`).value
          );
      }
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus = document.getElementById("cart-live-region-text");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  resetQuantity(elem) {
    if (!elem.hasAttribute("value")) return;
    elem.value = elem.dataset.currentQuantity;
  }

  enableLoading() {
    document
      .getElementById("main-cart-items")
      .classList.add("cart__items--disabled");
    this.querySelectorAll(".loading-overlay").forEach((loader) => {
      loader.classList.remove("hidden");
    });

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
    this.querySelectorAll('button[name="checkout"]').forEach((button) => {
      button.disabled = true;
    });
  }

  disableLoading() {
    // Update visibility free gift progressbar
    this.updateProgressBarVisibility();

    document
      .getElementById("main-cart-items")
      .classList.remove("cart__items--disabled");
    // Hide the loader
    this.querySelectorAll(".loading-overlay").forEach((loader) => {
      loader.classList.add("hidden");
    });
    // Enabled the button
    this.querySelectorAll('button[name="checkout"]').forEach((button) => {
      button.disabled = false;
    });
    // Update visibility shipping USPs based on item types
    let products = document.querySelectorAll('[data-item-type="product"]');
    const cartUSPs = document.getElementById("main-cart-usps");
    if (cartUSPs) {
      if (products.length >= 1) {
        cartUSPs.classList.remove("hidden");
      } else {
        cartUSPs.classList.add("hidden");
      }
    }

    //update visibility Pre order message
    const preOrderMessage = document.querySelector("[data-preorder-message]");
    let preOrder = false;
    if (preOrderMessage) {
      products.forEach((product) => {
        preOrder = product.dataset.preorder;
        if (preOrder == true) {
          preOrderMessage.classList.remove("hidden");
          return;
        } else if (preOrder == false) {
          preOrderMessage.classList.add("hidden");
        }
      });
    }
  }

  // Add a new helper method that only updates quantity
  updateQuantityOnly(lineIndex, quantity, state) {
    const body = JSON.stringify({
      line: lineIndex.toString(),
      quantity: quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((responseState) => {
        try {
          return JSON.parse(responseState);
        } catch (error) {
          console.error("Error parsing response:", error);
          return state;
        }
      })
      .catch((error) => {
        console.error("Error updating quantity only:", error);
        return state;
      });
  }

  // Helper method to calculate the total quantity needed for shared free products
  calculateTotalFreeProductQuantity(parsedState, freeItem) {
    let totalQuantity = 0;
    const processedProductIds = new Set(); // Track products we've already counted

    // DEBUG: Log what's in the cart
    console.log("Current cart state for calculation:", {
      total_items: parsedState.items.length,
      free_products: parsedState.items.filter(
        (item) => item.properties?._free_product === "true"
      ).length,
      regular_products: parsedState.items.filter(
        (item) => !item.properties?._free_product
      ).length,
      items: parsedState.items.map((item) => ({
        title: item.product_title,
        variant_id: item.variant_id,
        quantity: item.quantity,
        is_free: item.properties?._free_product === "true",
        parent_id:
          item.properties?._parent_id || item.properties?._main_product_variant,
      })),
    });

    // First, count only the explicit relationships - more reliable
    parsedState.items.forEach((item) => {
      // Skip free products themselves
      if (item.properties?._free_product === "true") return;

      // Skip if we've already counted this product
      const productKey = `${item.product_id}:${item.variant_id}`;
      if (processedProductIds.has(productKey)) return;

      // Get qualifying product keywords
      const productTitle = item.product_title?.toLowerCase() || "";
      const isBeachShorts = productTitle.includes("beach shorts");
      const isSummerDress = productTitle.includes("summer dress");

      // Only count products that should get free products
      if (isBeachShorts || isSummerDress) {
        totalQuantity += item.quantity;
        console.log(
          `Adding ${item.quantity} to free product total from ${item.title} (variant ${item.variant_id})`
        );
        processedProductIds.add(productKey);
      }
    });

    console.log(`Final calculated free product quantity: ${totalQuantity}`);
    return totalQuantity;
  }
}

if (!customElements.get("cart-items")) {
  customElements.define("cart-items", CartItems);
}
